import React, { useState } from "react";
import styled from "styled-components";
import { Badge } from "@opendate-app/setlist";

const StripeConnectWrapper = styled.div`
  .card {
    border: 1px solid var(--sl-border-secondary);

    .subtitle {
      font-size: 12px;
    }
  }

  .stripe-logo {
    width: 40px;
    height: 40px;
    background: #635bff;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    font-size: 18px;
  }

  .requirements-warning {
    background-color: var(--sl-bg-warning-primary);
    border: 1px solid var(--sl-border-warning);
    border-radius: 4px;
    padding: 12px;
    margin-top: 12px;
    font-size: 13px;
    color: var(--sl-text-warning);
  }
`;

const StripeConnect = ({
  requiresStripeConnect,
  hasAccount,
  chargesEnabled,
  payoutsEnabled,
  onboardingComplete,
  onboardUrl,
  requirements,
}) => {
  const [isConnecting, setIsConnecting] = useState(false);

  if (!requiresStripeConnect) {
    return null;
  }

  const handleConnectClick = () => {
    setIsConnecting(true);
  };

  const getStatusBadge = () => {
    if (chargesEnabled && payoutsEnabled) {
      return <Badge size="sm" color="success">Connected</Badge>;
    } else if (hasAccount) {
      return <Badge size="sm" color="warning">Setup Incomplete</Badge>;
    } else {
      return <Badge size="sm" color="error">Not Connected</Badge>;
    }
  };

  const getActionButton = () => {
    if (chargesEnabled) {
      return (
        <a
          href="https://dashboard.stripe.com"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary small"
        >
          <strong>Stripe Dashboard</strong>
          <i className="fas fa-external-link-alt" style={{ marginLeft: 6 }} />
        </a>
      );
    } else if (hasAccount) {
      return (
        <a
          href={onboardUrl}
          className="btn btn-primary small"
          onClick={handleConnectClick}
          style={isConnecting ? { pointerEvents: 'none', opacity: 0.6 } : {}}
        >
          <strong>{isConnecting ? 'Connecting...' : 'Complete Setup'}</strong>
        </a>
      );
    } else {
      return (
        <a
          href={onboardUrl}
          className="btn btn-primary small"
          onClick={handleConnectClick}
          style={isConnecting ? { pointerEvents: 'none', opacity: 0.6 } : {}}
        >
          <strong>{isConnecting ? 'Connecting...' : 'Connect Stripe'}</strong>
        </a>
      );
    }
  };

  const getStatusMessage = () => {
    if (chargesEnabled && payoutsEnabled) {
      return "Your Stripe account is connected and ready to accept payments in CAD.";
    } else if (hasAccount && onboardingComplete) {
      return "Your account is pending verification by Stripe.";
    } else if (hasAccount) {
      return "Please complete the Stripe onboarding process to start accepting payments.";
    } else {
      return "Connect your Stripe account to accept payments in CAD.";
    }
  };

  const getDisabledReasonMessage = (reason) => {
    const messages = {
      'requirements.past_due': 'There are past-due requirements on your account',
      'requirements.pending_verification': 'Your account is pending verification',
      'listed': 'Your account has been flagged for review',
      'platform_paused': 'Your account has been paused',
      'rejected.fraud': 'Your account was rejected due to suspected fraud',
      'rejected.listed': 'Your account was rejected',
      'rejected.terms_of_service': 'Your account was rejected due to a terms of service violation',
      'rejected.other': 'Your account was rejected',
      'under_review': 'Your account is under review',
    };
    return messages[reason] || 'There is an issue with your account';
  };

  const getRequirementsWarning = () => {
    if (!requirements) return null;

    const currentlyDue = requirements.currently_due || [];
    const eventuallyDue = requirements.eventually_due || [];
    const disabledReason = requirements.disabled_reason;

    if (disabledReason) {
      return (
        <div className="requirements-warning">
          <i className="fas fa-exclamation-triangle mr-2" />
          <strong>Account Restricted:</strong> {getDisabledReasonMessage(disabledReason)}.
          Please visit your Stripe Dashboard to resolve this issue.
        </div>
      );
    }

    if (currentlyDue.length > 0) {
      return (
        <div className="requirements-warning">
          <i className="fas fa-exclamation-triangle mr-2" />
          <strong>Action Required:</strong> Stripe requires additional information to keep your account active.
          Please visit your Stripe Dashboard to complete the requirements.
        </div>
      );
    }

    if (eventuallyDue.length > 0 && !chargesEnabled) {
      return (
        <div className="requirements-warning">
          <i className="fas fa-info-circle mr-2" />
          <strong>Pending Verification:</strong> Stripe is reviewing your account information.
        </div>
      );
    }

    return null;
  };

  return (
    <StripeConnectWrapper>
      <div className="row">
        <div className="col">
          <p className="mb-0 text-dark" style={{ fontSize: "16px" }}>
            <strong>Payment Processing</strong>
          </p>
          <p className="mb-0 text-muted small">
            Connect your Stripe account to process payments in Canadian Dollars.
          </p>
          <br />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-6">
          <div className="card">
            <div className="card-body px-3 py-3">
              <div className="row align-items-center">
                <div className="col-auto">
                  <div className="stripe-logo">S</div>
                </div>
                <div className="col">
                  <div className="d-flex align-items-center mb-1">
                    <strong className="mr-2">Stripe Connect</strong>
                    {getStatusBadge()}
                  </div>
                  <p className="text-muted m-0 subtitle">{getStatusMessage()}</p>
                </div>
                <div className="col-auto">{getActionButton()}</div>
              </div>
              {getRequirementsWarning()}
            </div>
          </div>
        </div>
      </div>
    </StripeConnectWrapper>
  );
};

export default StripeConnect;
